/*
   Filter schema. A page declares FilterDef[] once; the FilterPanel, chip
   strip, query serializer and useTable hook all consume the same shape.

   New filter kinds are added in one place: the discriminated union below,
   plus a matching field renderer in FilterPanel. Everything else — chips,
   URL params, active state, clear-all — is generic.
*/

export type FilterOption = { value: string; label: string }

export interface TextFilterDef {
  id: string
  label: string
  kind: "text"
  placeholder?: string
}

export interface SelectFilterDef {
  id: string
  label: string
  kind: "select"
  options: FilterOption[]
  /** When true, the value is an array of option values; otherwise a single value. */
  multi?: boolean
  placeholder?: string
}

export interface DateRangeFilterDef {
  id: string
  label: string
  kind: "date-range"
}

export interface NumberRangeFilterDef {
  id: string
  label: string
  kind: "number-range"
  min?: number
  max?: number
}

export interface BooleanFilterDef {
  id: string
  label: string
  kind: "boolean"
  /** Optional description under the toggle. */
  hint?: string
}

export type FilterDef =
  TextFilterDef | SelectFilterDef | DateRangeFilterDef | NumberRangeFilterDef | BooleanFilterDef

/** Range value shape for date-range and number-range filters. */
export interface RangeValue<T> {
  from: T | null
  to: T | null
}

/** The runtime value type for a given filter kind. */
export type FilterValue =
  string | string[] | boolean | RangeValue<string> | RangeValue<number> | null | undefined

export type FilterValues = Record<string, FilterValue>

/** True when the filter is effectively unset. */
export function isFilterActive(value: FilterValue): boolean {
  if (value === null || value === undefined) return false
  if (typeof value === "string") return value.length > 0
  if (typeof value === "boolean") return value === true
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === "object") return value.from != null || value.to != null
  return false
}

/** Strip inactive filters — used before shipping to the API or URL. */
export function activeFilters(values: FilterValues): FilterValues {
  const out: FilterValues = {}
  for (const [k, v] of Object.entries(values)) {
    if (isFilterActive(v)) out[k] = v
  }
  return out
}

/** Count of active filter axes — for the "Filters (3)" button badge. */
export function activeFilterCount(values: FilterValues): number {
  let n = 0
  for (const v of Object.values(values)) {
    if (isFilterActive(v)) n++
  }
  return n
}
